import React, { useState, useMemo, useEffect } from 'react';
import '../TracePanelStyles.css';
import { Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import AdbIcon from '@material-ui/icons/Adb';
import MemoryIcon from '@material-ui/icons/Memory';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import { useTranslation } from 'react-i18next';
import {
    formatDuration,
    JsonView,
    DetailSection,
    TracePanelHeader,
    TraceDetailTabs,
    TraceSelectableRow,
    formatCountLabel,
    handleKeyActivate,
    useLazyAnswerPanelData,
    PanelLoadingState,
    PanelLoadError
} from '../TracePanelShared';

const buildTree = (traces) => {
    if (!traces || traces.length === 0) return [];
    const map = {};
    const roots = [];
    traces.forEach(trace => { map[trace['span-id']] = { ...trace, children: [] }; });
    traces.forEach(trace => {
        const node = map[trace['span-id']];
        if (trace['parent-id'] && map[trace['parent-id']]) {
            map[trace['parent-id']].children.push(node);
        } else { roots.push(node); }
    });
    return roots;
};

const getRunIcon = (runType) => {
    switch (runType?.toLowerCase()) {
        case 'llm': return <MemoryIcon className="icon-llm" style={{fontSize: 16}} />;
        case 'tool': return <FlashOnIcon className="icon-tool" style={{fontSize: 16}} />;
        default: return <AdbIcon className="icon-chain" style={{fontSize: 16}} />;
    }
};

const TraceNode = ({ node, depth, selectedNode, onSelect, expandedNodes, onToggleExpand }) => {
    const isExpanded = expandedNodes.includes(node['span-id']);
    const isSelected = selectedNode?.['span-id'] === node['span-id'];
    const hasChildren = node.children && node.children.length > 0;

    const handleToggleExpand = (event) => {
        event.stopPropagation();
        onToggleExpand(node['span-id']);
    };

    return (
        <div className="trace-node">
            <TraceSelectableRow
                isSelected={isSelected}
                onSelect={() => onSelect(node)}
                style={{ paddingLeft: `${depth * 16 + 12}px` }}
            >
                <div
                    className="trace-toggle"
                    role="button"
                    tabIndex={0}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    onClick={handleToggleExpand}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        handleKeyActivate(e, () => onToggleExpand(node['span-id']));
                    }}
                >
                    {hasChildren ? (isExpanded ? <ExpandMoreIcon style={{fontSize: 14}}/> : <ChevronRightIcon style={{fontSize: 14}}/>) : null}
                </div>
                <div className="trace-icon">
                    {getRunIcon(node['run-type'])}
                </div>
                <div className="trace-name">
                    {node.name}
                </div>
                <div className="trace-pills">
                    <span className="pill pill-duration">{formatDuration(node['duration-ms'])}</span>
                    {node.tokens && <span className="pill pill-tokens">{node.tokens}</span>}
                </div>
            </TraceSelectableRow>
            {hasChildren && isExpanded && (
                <div className="trace-children">
                    {node.children.map(child => (
                        <TraceNode
                            key={child['span-id']}
                            node={child}
                            depth={depth + 1}
                            selectedNode={selectedNode}
                            onSelect={onSelect}
                            expandedNodes={expandedNodes}
                            onToggleExpand={onToggleExpand}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const ConversationItemAgentTraces = ({ projectId, conversationId, answerId }) => {
    const { t, i18n } = useTranslation();
    const [detailTab, setDetailTab] = useState('attributes');
    const [selectedNode, setSelectedNode] = useState(null);
    const [expandedNodes, setExpandedNodes] = useState([]);

    const {
        data: traces,
        isLoading,
        loadError,
        isExpanded,
        isLoaded,
        hasData,
        handleToggle,
        fetchData
    } = useLazyAnswerPanelData({
        projectId,
        conversationId,
        answerId,
        resource: 'traces',
        t,
        collapseWhenEmpty: true
    });

    const traceList = useMemo(() => traces ?? [], [traces]);
    const countLabel = !isLoaded ? null : formatCountLabel(traceList.length, t, i18n, 'run');
    const tree = useMemo(() => (hasData ? buildTree(traceList) : []), [traceList, hasData]);
    const headerDisabled = isLoaded && !hasData && !loadError;

    useEffect(() => {
        if (!hasData) {
            setSelectedNode(null);
            setExpandedNodes([]);
            return;
        }
        setSelectedNode(tree[0] || null);
        setExpandedNodes(traceList.map((trace) => trace['span-id']));
    }, [traceList, hasData, tree]);

    const detailTabs = [
        { id: 'feedback', label: t('Feedback') },
        { id: 'input', label: t('Input') },
        { id: 'output', label: t('Output') },
        { id: 'attributes', label: t('Attributes') }
    ];

    return (
        <div className={`trace-container ${isExpanded ? 'is-expanded' : ''}`}>
            <TracePanelHeader
                title={t('Agent Traces')}
                countLabel={countLabel}
                isExpanded={isExpanded}
                onToggle={handleToggle}
                disabled={headerDisabled}
            />

            {isExpanded && (
                <div className="trace-content">
                    {isLoading ? (
                        <PanelLoadingState />
                    ) : loadError ? (
                        <PanelLoadError
                            message={loadError}
                            onRetry={fetchData}
                            retryLabel={t('Try again')}
                        />
                    ) : !hasData ? (
                        <div style={{ padding: '24px', color: '#70757a', fontSize: '13px', width: '100%', textAlign: 'center' }}>
                            {t('No traces found for this conversation turn.')}
                        </div>
                    ) : (
                        <>
                            <div className="trace-tree">
                                {tree.map(node => (
                                    <TraceNode
                                        key={node['span-id']}
                                        node={node}
                                        depth={0}
                                        selectedNode={selectedNode}
                                        onSelect={setSelectedNode}
                                        expandedNodes={expandedNodes}
                                        onToggleExpand={(id) => setExpandedNodes(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                                    />
                                ))}
                            </div>
                            <div className="trace-detail">
                                {selectedNode && (
                                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                        <div className="detail-header">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                {getRunIcon(selectedNode['run-type'])}
                                                <Typography variant="subtitle1" style={{ color: '#3c4043', fontWeight: 600 }}>
                                                    {selectedNode.name}
                                                </Typography>
                                            </div>
                                            <Typography variant="caption" style={{ color: '#70757a' }}>
                                                ID: {selectedNode['span-id']}
                                            </Typography>
                                        </div>
                                        <TraceDetailTabs
                                            tabs={detailTabs}
                                            activeTab={detailTab}
                                            onTabChange={setDetailTab}
                                        />
                                        <div className="detail-body">
                                            {detailTab === 'feedback' && (
                                                <div style={{ color: '#70757a', textAlign: 'center', marginTop: '40px' }}>
                                                    {t('No feedback available')}
                                                </div>
                                            )}
                                            {detailTab === 'input' && (
                                                <DetailSection title={t('Inputs')} defaultExpanded={true}>
                                                    <JsonView data={selectedNode.inputs} />
                                                </DetailSection>
                                            )}
                                            {detailTab === 'output' && (
                                                <DetailSection title={t('Outputs')} defaultExpanded={true}>
                                                    <JsonView data={selectedNode.outputs} />
                                                </DetailSection>
                                            )}
                                            {detailTab === 'attributes' && (
                                                <>
                                                    <DetailSection title={t('Metadata')} defaultExpanded={true}>
                                                        {selectedNode.metadata && Object.entries(selectedNode.metadata).map(([k, v]) => (
                                                            <div key={k} className="detail-meta-row">
                                                                <div className="detail-meta-key">{k}</div>
                                                                <div className="detail-meta-value">{String(v)}</div>
                                                            </div>
                                                        ))}
                                                        <div className="detail-meta-row">
                                                            <div className="detail-meta-key">duration</div>
                                                            <div className="detail-meta-value">{formatDuration(selectedNode['duration-ms'])}</div>
                                                        </div>
                                                    </DetailSection>
                                                    {selectedNode.tags && selectedNode.tags.length > 0 && (
                                                        <DetailSection title={t('Tags')}>
                                                            {selectedNode.tags.map(tag => (
                                                                <span key={tag} className="tag-pill">{tag}</span>
                                                            ))}
                                                        </DetailSection>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ConversationItemAgentTraces;
